import { useEffect, useState } from 'react';
import { ApiClient } from '@locus-taxy/encore-ui-network';

interface UsePhotoUrlParams {
  fileId: string | null | undefined;
  clientId: string;
}

interface UsePhotoUrlReturn {
  photoUrl: string | null;
  loading: boolean;
  error: string | null;
}

/**
 * Resolves a stored file id to an object URL for an `<img src>`.
 *
 * The fetch is owned by the effect rather than a `useCallback`, so each run
 * can disown its own in-flight request. Two things depend on that:
 *
 * - **Stale responses never win.** When `fileId` or `clientId` changes while a
 *   request is in flight, the superseded response is dropped instead of
 *   overwriting the newer one — otherwise a slow request for the previous
 *   account could paint its photo over the current one. This matters most in
 *   the sidebar footer, which stays mounted across tenant switches.
 * - **Every object URL is revoked.** A URL created after its effect was torn
 *   down is revoked immediately rather than leaked, which the previous
 *   closure-captured cleanup could not do: it read the variable before the
 *   promise had assigned it.
 */
export function usePhotoUrl({
  fileId,
  clientId,
}: UsePhotoUrlParams): UsePhotoUrlReturn {
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!fileId) {
      setPhotoUrl(null);
      setLoading(false);
      setError(null);
      return;
    }

    let superseded = false;
    let createdUrl: string | null = null;

    // Drop the previous photo up front: its object URL is revoked by this
    // effect's cleanup, so continuing to render it would show a dead blob.
    setPhotoUrl(null);
    setLoading(true);
    setError(null);

    const run = async () => {
      try {
        const apiClient = ApiClient.getInstance();
        const endpoint = `/encore/api/v1/view/clients/${clientId}/files/${fileId}`;

        const response = await apiClient.get<Blob>(endpoint, {
          responseType: 'blob',
        });

        const blob = response instanceof Blob ? response : (response as Blob);

        if (!(blob instanceof Blob)) {
          throw new Error('Invalid response format - expected Blob');
        }

        if (blob.size === 0) {
          throw new Error('Received empty image file');
        }

        createdUrl = URL.createObjectURL(blob);

        if (superseded) {
          // Nothing will ever render this one — revoke it here, because the
          // cleanup that would have done so has already run.
          URL.revokeObjectURL(createdUrl);
          createdUrl = null;
          return;
        }

        setPhotoUrl(createdUrl);
      } catch (err) {
        if (superseded) {
          return;
        }
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to load photo';
        setError(errorMessage);
        setPhotoUrl(null);
      } finally {
        if (!superseded) {
          setLoading(false);
        }
      }
    };

    void run();

    return () => {
      superseded = true;
      if (createdUrl) {
        URL.revokeObjectURL(createdUrl);
        createdUrl = null;
      }
    };
  }, [fileId, clientId]);

  return {
    photoUrl,
    loading,
    error,
  };
}
